import type { Deal } from "@/api/client";
import { ChannelPartnerSection } from "@/components/deals/ChannelPartnerSection";
import { DealInternalNotesSection } from "@/components/deals/DealInternalNotesSection";
import { DealManagerCloseActions } from "@/components/deals/DealManagerCloseActions";
import { DealNotesSection } from "@/components/deals/DealNotesSection";
import { DealProfileSections } from "@/components/deals/DealProfileSections";
import { DealSpifTracker } from "@/components/deals/DealSpifTracker";
import { DealStatusSection } from "@/components/deals/DealStatusSection";
import { DetailSections } from "@/components/ui/section";

/**
 * Read-only CM/RA/RM deal detail (CHP-241/243/245/246) for the non-review
 * states (live / denied / closed_won / closed_lost): Status (manager view,
 * with the reviewer) → Actions (live only) → The Deal (Channel Partner +
 * the shared profile sections). Rendered inside the manager review drawer /
 * route, which supplies the header chrome ([Client Name] / [Deal ID]).
 *
 * The live-deal Actions are the functional manager-initiated Closed-Won /
 * Closed-Lost flows (CHP-243/CHP-255), powered by the manager-scoped backend
 * (win-approve from LIVE + owner-or-manager close). `onDone` runs after the
 * action's toast + 1.5s hold (drawer → close; route → navigate to dashboard).
 */
export function ManagerDealDetail({ deal, onDone }: { deal: Deal; onDone: () => void }) {
  return (
    <DetailSections>
      <DealStatusSection deal={deal} viewer="manager" />

      {deal.deal_status === "live" ? (
        <section>
          <h2 className="mb-3 text-base font-semibold">Actions</h2>
          <DealManagerCloseActions deal={deal} onDone={onDone} />
        </section>
      ) : null}

      <section>
        <h2 className="mb-4 text-base font-semibold">The Deal</h2>
        <DetailSections>
          <ChannelPartnerSection deal={deal} />
          <DealProfileSections deal={deal} />
        </DetailSections>
      </section>

      {/* Rewards earned (CHP-406) — the same DealSpifTracker the BDM/PO detail
          uses (CHP-391). For a closed-won deal it lists the approved SPIF bonus
          + approved meeting rewards; the GET /deals/{id}/meeting-reward and
          profile endpoints it reads are both accessible to CM/RA/RM, so it
          renders unchanged in the manager view. Renders nothing pre-adjudication. */}
      <DealSpifTracker deal={deal} />

      {/* Deal Owner Notes (CHP-340 / CHP-401 relabel) — the BDM/PO-authored
          notes, read-only for CM/RA/RM (the section hides the add input + edit
          affordance for non-owning managers). The CM/RA/RM view labels them
          "Deal Owner Notes"; the partner view's "Deal Progress" relabel lives
          in the live-detail lane (split-by-view ruling). */}
      <DealNotesSection deal={deal} heading="Deal Owner Notes" />

      {/* Internal notes (CHP-401) — CM/RA/RM-only decision notes on a separate
          table. Never reaches a BDM/PO (the section + backend are manager-only). */}
      <DealInternalNotesSection deal={deal} />
    </DetailSections>
  );
}
